import React, { useEffect, useRef, useState } from 'react';

// Seamless horizontal scroller used by the ticker strips and the
// information line. It measures one set of children and the visible width,
// repeats the set enough times to fill the line, and moves exactly one
// set's width per loop — no gap or jump, in either direction, at any
// screen width. Pauses on hover/focus (CSS); with "reduce motion" the
// copies are hidden and nothing moves (CSS).
// Speed: pxPerSecond (reading pace) or loopSeconds (one full loop).
export default function Marquee({ render, direction = 'left', pxPerSecond = 40, loopSeconds = null, deps = [], label }) {
  const viewRef = useRef(null);
  const groupRef = useRef(null);
  const [size, setSize] = useState(null);

  useEffect(() => {
    const measure = () => {
      const g = groupRef.current?.offsetWidth || 0;
      const v = viewRef.current?.clientWidth || 0;
      if (g && v) setSize((cur) => (cur && cur.group === g && cur.view === v ? cur : { group: g, view: v }));
    };
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (ro) { ro.observe(viewRef.current); ro.observe(groupRef.current); }
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => { if (ro) ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const copies = size ? Math.max(1, Math.ceil(size.view / size.group)) : 1;
  const seconds = size ? (loopSeconds || size.group / pxPerSecond) : 0;
  const style = size ? { '--ticker-shift': `-${size.group}px`, '--ticker-duration': `${Math.max(5, seconds).toFixed(1)}s` } : undefined;
  return (
    <div className="ticker-viewport" tabIndex={0} ref={viewRef} aria-label={label}>
      <div className={`ticker-track is-${direction === 'right' ? 'right' : 'left'}${size ? ' is-ready' : ''}`} style={style}>
        <div className="ticker-group" ref={groupRef}>{render(false)}</div>
        {Array.from({ length: copies }, (_, k) => (
          <div key={k} className="ticker-group ticker-copy" aria-hidden="true">{render(true)}</div>
        ))}
      </div>
    </div>
  );
}
